import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Table } from 'primeng/table';
import { Menu } from 'primeng/menu';
import { TranslateService } from '@ngx-translate/core';
import { MessageService, MenuItem } from 'primeng/api';

import { BaseServices } from '../../kuba.services';
import { SdsHazardStatementRecord, SdsRecord } from '../models/sds.model';
import {
  DEFAULT_SDS_UI_PICTOGRAMS,
  SdsApiService,
  SdsUiPictogramOption
} from '../services/sds-api.service';
import { SdsPublicApiService } from '../services/sds-public-api.service';
import { SdsListQrCode } from '../models/sds-public.model';

const QR_PRINT_CANVAS_SIZE = 720;
const QR_PRINT_JPEG_QUALITY = 0.82;

@Component({
  selector: 'sds-list',
  templateUrl: 'sds-list.component.html',
  styleUrls: ['sds-list.component.css', './sds-theme.css'],
  providers: [],
  standalone: false
})
export class SdsListComponent implements OnInit, OnDestroy {
  @ViewChild('sdsTable', { static: false }) sdsTable: Table;
  @ViewChild('rowActionsMenu', { static: false }) rowActionsMenu?: Menu;

  /** Model for the per-row Actions dropdown; rebuilt each time it's opened. */
  actionMenuItems: MenuItem[] = [];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
    private messageService: MessageService,
    private sdsApiService: SdsApiService,
    private sdsPublicApiService: SdsPublicApiService
  ) {}

  sdsRecords: SdsRecord[] = [];
  /** True while a page is being fetched — drives the loading skeleton / table overlay. */
  loading = true;

  /* ─── Server-side pagination state ─── */
  totalRecords = 0;
  pageSize = 10;
  first = 0;
  private pageNumber = 1;
  searchTerm = '';
  riskFilter = '';
  sortColumn: string | null = null;
  sortDirection: 'ASC' | 'DESC' = 'DESC';
  private searchDebounceHandle: ReturnType<typeof setTimeout> | null = null;
  sidebarVisible = false;
  selectedSds: SdsRecord | null = null;

  /** Max H-statement code badges shown in a list row; the rest collapse into a "+N" badge. */
  readonly hCodeDisplayLimit = 10;

  confirmDeleteVisible = false;
  recordToDelete: SdsRecord | null = null;

  confirmArchiveVisible = false;
  recordToArchive: SdsRecord | null = null;

  pdfPreviewVisible = false;
  pdfPreviewUrl: SafeResourceUrl | null = null;
  pdfPreviewLoading = false;
  pdfPreviewFileName = '';
  private pdfBlobUrl: string | null = null;
  private pdfPreviewRequest = 0;
  pdfPreviewRecord: SdsRecord | null = null;

  isGeneratingShareLink = false;
  sidebarQrCodeImageUrl = '';

  /* ─── Public chemical-list QR code (one per business, shared by the whole list) ─── */
  listQrVisible = false;
  listQrCode: SdsListQrCode | null = null;
  listQrLoading = false;
  /** Only Admin, Portal and Editor may generate or revoke; everyone else can look. */
  canManageListQr = [1, 2, 3].includes(Number(BaseServices.roleId));

  /** Substitution-assessment modal. */
  businessId = BaseServices.BusinessId;
  substitutionVisible = false;
  bulkImportVisible = false;

  onBulkImported(): void {
    this.searchTerm = '';
    this.riskFilter = '';
    this.pageNumber = 1;
    this.first = 0;
    this.sortColumn = null;
    this.sortDirection = 'DESC';
    this.updateUrlState();
    this.fetchPage();
  }

  /** True when this list renders the Archived Chemicals view (replaced + deleted). */
  isArchivedView = false;

  pictogramMap: Record<string, { label: string; ghs: string; svg: string }> =
    this.buildPictogramMap(DEFAULT_SDS_UI_PICTOGRAMS);

  ngOnInit(): void {
    document.querySelector('body')!.classList.remove('opened');
    this.isArchivedView = this.route.snapshot.data['archived'] === true;
    // Restore paging, search, risk and sort from the URL so returning from a detail view
    // (via browser back) lands on the same page the user left.
    this.restoreStateFromUrl();
    this.loadPictograms();
    this.fetchPage();
  }

  /** Reads list state back from the URL query params (set by {@link updateUrlState}). */
  private restoreStateFromUrl(): void {
    const qp = this.route.snapshot.queryParamMap;
    const risk = (qp.get('risk') ?? '').toLowerCase();
    this.riskFilter = ['low', 'medium', 'high'].includes(risk) ? risk : '';

    const size = Number(qp.get('size'));
    if ([5, 10, 20].includes(size)) {
      this.pageSize = size;
    }

    const page = Number(qp.get('page'));
    if (page > 0) {
      this.pageNumber = page;
      this.first = (page - 1) * this.pageSize;
    }

    const search = qp.get('q');
    if (search) {
      this.searchTerm = search;
    }

    const sort = qp.get('sort');
    if (sort) {
      this.sortColumn = sort;
      const dir = qp.get('dir');
      this.sortDirection = dir === 'ASC' ? 'ASC' : 'DESC';
    }
  }

  /**
   * Mirrors the current paging, search, risk and sort into the URL (replacing history, not
   * stacking it) so it survives a round-trip to a detail record. Only non-default
   * values are written to keep the URL clean. Routing out of the tab drops the
   * params, so re-entering the tab starts fresh.
   */
  private updateUrlState(): void {
    const queryParams: { [key: string]: string | number } = {};
    if (this.riskFilter) {
      queryParams['risk'] = this.riskFilter;
    }
    if (this.pageNumber > 1) {
      queryParams['page'] = this.pageNumber;
    }
    if (this.pageSize !== 10) {
      queryParams['size'] = this.pageSize;
    }
    if (this.searchTerm) {
      queryParams['q'] = this.searchTerm;
    }
    if (this.sortColumn) {
      queryParams['sort'] = this.sortColumn;
      queryParams['dir'] = this.sortDirection;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true
    });
  }

  /** PrimeNG lazy paging — fires when the user changes page or page size. */
  onPage(event: { first?: number; rows?: number }): void {
    if (this.loading) {
      return;
    }
    this.pageSize = event.rows ?? this.pageSize;
    this.first = event.first ?? 0;
    this.pageNumber = Math.floor(this.first / this.pageSize) + 1;
    this.updateUrlState();
    this.fetchPage();
  }

  /** PrimeNG lazy sort — fires when the user clicks a sortable column header. */
  onSort(event: { field?: string; order?: number }): void {
    const nextColumn = event.field ?? null;
    const nextDirection: 'ASC' | 'DESC' = event.order === -1 ? 'DESC' : 'ASC';

    if (nextColumn === this.sortColumn && nextDirection === this.sortDirection) {
      return;
    }

    this.sortColumn = nextColumn;
    this.sortDirection = nextDirection;
    this.resetToFirstPage();
    this.updateUrlState();
    this.fetchPage();
  }

  /** Server-side global search, debounced so we don't fire on every keystroke. */
  onSearch(value: string): void {
    this.searchTerm = (value || '').trim();
    this.resetToFirstPage();

    if (this.searchDebounceHandle) {
      clearTimeout(this.searchDebounceHandle);
    }
    this.searchDebounceHandle = setTimeout(() => {
      this.updateUrlState();
      this.fetchPage();
    }, 300);
  }

  private resetToFirstPage(): void {
    this.pageNumber = 1;
    this.first = 0;
  }

  clearRiskFilter(): void {
    this.riskFilter = '';
    this.resetToFirstPage();
    this.updateUrlState();
    this.fetchPage();
  }

  addNewRecord(): void {
    this.router.navigate(['../new'], { relativeTo: this.route });
  }

  openSubstitutionAssessment(): void {
    this.substitutionVisible = true;
  }

  /** Refresh the list after an assess/replace so any derived data stays current. */
  onSubstitutionChanged(): void {
    this.fetchPage();
  }

  getRiskFigureLabel(valueText: string | null | undefined): string {
    switch ((valueText ?? '').trim().toLowerCase()) {
      case 'low':
        return this.translate.instant('SDS_RISK_LOW');
      case 'medium':
        return this.translate.instant('SDS_RISK_MEDIUM');
      case 'high':
        return this.translate.instant('SDS_RISK_HIGH');
      default:
        return '';
    }
  }

  hasRiskFigure(record: SdsRecord | null | undefined): boolean {
    return ( !!record?.RiskFigure && record.RiskFigure.Value > 0 && !!this.getRiskFigureLabel(record.RiskFigure.ValueText));
  }

  requiresCompletion(record: SdsRecord): boolean {
    return !record.ProductName.trim() || !record.Supplier.trim() ||
      (!record.NoCasNumber && !record.CASNumber?.trim()) || !this.hasRiskFigure(record);
  }

  /** Split the comma-joined H-statement codes into individual badge values. */
  splitHStatementCodes(value: string | null | undefined): string[] {
    return (value || '')
      .split(',')
      .map(code => code.trim())
      .filter(code => code.length > 0);
  }

  /** Full "H317 — <text>" wording for a hazard-statement badge's tooltip. */
  formatHazardStatement(statement: SdsHazardStatementRecord): string {
    return this.sdsApiService.formatHazardStatement(statement, this.translate.currentLang());
  }

  openDetail(record: SdsRecord): void {
    // Archived/deleted chemicals open read-only — they're history, not editable.
    // Everything else is reached via the "Edit" action, so open straight in edit mode.
    const extras = this.isArchivedView
      ? { relativeTo: this.route, queryParams: { readonly: 'true' } }
      : { relativeTo: this.route, queryParams: { edit: 'true' } };
    this.router.navigate(['../detail', record.Id], extras);
  }

  openSdsSidebar(record: SdsRecord): void {
    this.selectedSds = record;
    this.sidebarQrCodeImageUrl = record.QrShareUrl ? this.buildQrImageUrl(record.QrShareUrl) : '';
    this.sidebarVisible = true;
  }

  closeSidebar(): void {
    this.sidebarVisible = false;
    this.selectedSds = null;
    this.sidebarQrCodeImageUrl = '';
  }

  async openPdfPreview(record: SdsRecord): Promise<void> {
    this.closeSidebar();
    this.revokePdfBlobUrl();
    const request = ++this.pdfPreviewRequest;
    this.pdfPreviewRecord = record;
    this.pdfPreviewFileName = record.SdsFileName;
    this.pdfPreviewVisible = true;
    this.pdfPreviewLoading = true;

    try {
      let file: Blob;
      if (record.SdsFileData) {
        file = this.base64ToBlob(record.SdsFileData, 'application/pdf');
      } else if (record.SdsFileUrl || record.SelectedFileVersionId) {
        file = await this.sdsApiService
          .getDocumentFile(record.Id, record.SelectedFileVersionId, record.SdsFileName, 'application/pdf')
          .toPromise();
      } else {
        return;
      }

      // A closed preview or a newer selection must not display this response.
      if (request !== this.pdfPreviewRequest) {
        return;
      }
      if (!file) {
        throw new Error('The selected SDS file could not be loaded.');
      }
      this.pdfBlobUrl = URL.createObjectURL(file);
      this.pdfPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfBlobUrl + '#zoom=100');
    } catch (_) {
      if (request === this.pdfPreviewRequest) {
        this.messageService.add({
          severity: 'error',
          summary: 'SDS',
          detail: this.translate.instant('SDS_PDF_NOT_AVAILABLE')
        });
      }
    } finally {
      if (request === this.pdfPreviewRequest) {
        this.pdfPreviewLoading = false;
      }
    }
  }

  closePdfPreview(): void {
    ++this.pdfPreviewRequest;
    this.pdfPreviewVisible = false;
    this.pdfPreviewLoading = false;
    this.revokePdfBlobUrl();
  }

  async generateQrForSelectedSds(): Promise<void> {
    if (!this.selectedSds) {
      return;
    }

    await this.generateQrForRecord(this.selectedSds);
    this.sidebarQrCodeImageUrl = this.selectedSds.QrShareUrl
      ? this.buildQrImageUrl(this.selectedSds.QrShareUrl)
      : '';
  }

  async printQrA4ForSelectedSds(): Promise<void> {
    if (!this.selectedSds) {
      return;
    }

    await this.printQrA4(this.selectedSds);
    this.sidebarQrCodeImageUrl = this.selectedSds.QrShareUrl
      ? this.buildQrImageUrl(this.selectedSds.QrShareUrl)
      : '';
  }

  openSelectedSdsShareUrl(): void {
    if (!this.selectedSds?.QrShareUrl) {
      return;
    }

    window.open(this.selectedSds.QrShareUrl, '_blank', 'noopener');
  }

  copySelectedSdsShareUrl(): void {
    if (!this.selectedSds?.QrShareUrl) {
      return;
    }

    navigator.clipboard?.writeText(this.selectedSds.QrShareUrl);
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('SDS_COPIED'),
      detail: this.translate.instant('SDS_SHARE_LINK_COPIED')
    });
  }

  // ==================== PUBLIC CHEMICAL-LIST QR CODE ====================

  /**
   * Opens the QR dialog and loads the business' current code, if it has one.
   * The code is fetched on open rather than with the list, so the ordinary list page costs
   * nothing extra for the users who never share it.
   */
  openListQrDialog(): void {
    this.listQrVisible = true;
    this.loadListQrCode();
  }

  private loadListQrCode(): void {
    this.listQrLoading = true;

    this.sdsPublicApiService.getBusinessQrCode(BaseServices.BusinessId).subscribe({
      next: qrCode => {
        // 204 No Content arrives as an empty body: the business simply has no code yet.
        this.listQrCode = qrCode ?? null;
        this.listQrLoading = false;
      },
      error: () => {
        this.listQrCode = null;
        this.listQrLoading = false;
      }
    });
  }

  /** Generate, or regenerate: the backend revokes the previous code as part of the same call. */
  onCreateListQrCode(expiresOn: string | null): void {
    this.listQrLoading = true;

    this.sdsPublicApiService
      .createQrCode({ BusinessId: BaseServices.BusinessId, ExpiresOn: expiresOn })
      .subscribe({
        next: qrCode => {
          this.listQrCode = qrCode;
          this.listQrLoading = false;
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('SDS_PUBLIC_LIST_QR_TITLE'),
            detail: this.translate.instant('SDS_PUBLIC_LIST_QR_GENERATED')
          });
        },
        error: () => {
          this.listQrLoading = false;
        }
      });
  }

  onRevokeListQrCode(): void {
    this.listQrLoading = true;

    this.sdsPublicApiService.revokeQrCode(BaseServices.BusinessId).subscribe({
      next: () => {
        this.listQrCode = null;
        this.listQrLoading = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('SDS_PUBLIC_LIST_QR_TITLE'),
          detail: this.translate.instant('SDS_PUBLIC_LIST_QR_REVOKED')
        });
      },
      error: () => {
        this.listQrLoading = false;
      }
    });
  }

  private fetchPage(): void {
    this.loading = true;
    this.sdsApiService
      .getBusinessDocumentRecordsPaged(BaseServices.BusinessId, {
        pageNumber: this.pageNumber,
        pageSize: this.pageSize,
        search: this.searchTerm || null,
        sortColumn: this.sortColumn,
        sortDirection: this.sortColumn ? this.sortDirection : null,
        archived: this.isArchivedView
      }, this.riskFilter || null)
      .subscribe({
        next: result => {
          this.sdsRecords = result.records;
          this.totalRecords = result.totalCount;
          this.loading = false;
        },
        error: error => {
          this.loading = false;
          this.sdsRecords = [];
          this.totalRecords = 0;

          if (error?.status === 404) {
            return;
          }

          this.messageService.add({
            severity: 'error',
            summary: 'SDS',
            detail: 'Could not load safety data sheets from the backend.'
          });
        }
      });
  }

  private loadPictograms(): void {
    this.sdsApiService.getPictogramOptions().subscribe({
      next: options => {
        this.pictogramMap = this.buildPictogramMap(options);
      },
      error: error => {
        if (error?.status !== 404) {
          this.messageService.add({
            severity: 'warn',
            summary: 'SDS',
            detail: 'Could not load pictograms from the backend. Showing fallback icons.'
          });
        }
      }
    });
  }

  private buildPictogramMap(
    options: SdsUiPictogramOption[]
  ): Record<string, { label: string; ghs: string; svg: string }> {
    return options.reduce((map, option) => {
      map[option.id] = {
        label: option.label,
        ghs: option.ghs,
        svg: option.svg
      };

      return map;
    }, {} as Record<string, { label: string; ghs: string; svg: string }>);
  }

  private buildQrImageUrl(url: string): string {
    return `https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(url)}`;
  }

  private async generateQrForRecord(record: SdsRecord): Promise<void> {
    try {
      this.isGeneratingShareLink = true;
      if (!record.SdsFileUrl) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('SDS_QR_CODE'),
          detail: this.translate.instant('SDS_QR_NO_SHAREABLE_PDF')
        });
        return;
      }

      const response = await this.sdsApiService
        .createResolvedShareLink(record.Id, {
          FileVersionId: record.SelectedFileVersionId ?? null
        })
        .toPromise();

      if (!response) {
        return;
      }

      if (!response.ShareUrl) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('SDS_QR_CODE'),
          detail: this.translate.instant('SDS_QR_GENERATE_FAILED')
        });
        return;
      }

      this.persistRecord(this.sdsApiService.updateRecordFileVersionShare(
        record,
        response.FileVersionId,
        response.ShareUrl,
        response.ExpiresOnUtc,
        response.ShareLinkId
      ));
    } catch (_) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('SDS_QR_CODE'),
        detail: this.translate.instant('SDS_QR_GENERATE_FAILED')
      });
    } finally {
      this.isGeneratingShareLink = false;
    }
  }

  private async printQrA4(record: SdsRecord): Promise<void> {
    if (!record.QrShareUrl) {
      await this.generateQrForRecord(record);
    }

    if (!record.QrShareUrl) {
      return;
    }

    const qrImageUrl = this.buildQrImageUrl(record.QrShareUrl);
    const brandedQrDataUrl = await this.buildBrandedQrDataUrl(qrImageUrl);
    const title = record.ProductName || 'SDS QR';
    const fileName = record.SdsFileName || '';

    this.printQrImage(brandedQrDataUrl, title, fileName);
  }

  private printQrImage(imageDataUrl: string, title: string, fileName: string = ''): void {
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';

    document.body.appendChild(printFrame);

    const printWindow = printFrame.contentWindow;
    const printDocument = printFrame.contentDocument || printWindow?.document;

    if (!printWindow || !printDocument) {
      document.body.removeChild(printFrame);
      return;
    }

    const escapedTitle = this.escapeHtml(title);
    const escapedFileName = this.escapeHtml(fileName);

    printWindow.onafterprint = () => {
      setTimeout(() => {
        if (printFrame.parentNode) {
          printFrame.parentNode.removeChild(printFrame);
        }
      }, 500);
    };

    printDocument.open();
    printDocument.write(`
<!doctype html>
<html>
  <head>
    <title>${escapedTitle}</title>
    <style>
      @page { size: A4; margin: 0; }
      html,
      body {
        width: 100%;
        height: 100%;
        margin: 0;
      }
      body {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        font-family: Arial, sans-serif;
      }
      .qr-title {
        margin: 0 0 4mm;
        font-size: 14pt;
        font-weight: bold;
        text-align: center;
      }
      img {
        width: 170mm;
        height: 170mm;
        object-fit: contain;
      }
      .qr-file-name {
        margin-top: 4mm;
        font-size: 11pt;
        text-align: center;
        word-break: break-all;
      }
    </style>
  </head>
  <body>
    <div class="qr-title">${escapedTitle}</div>
    <img src="${imageDataUrl}" alt="${escapedTitle}" onload="setTimeout(function(){ window.focus(); window.print(); }, 50)" />
    ${escapedFileName ? `<div class="qr-file-name">${escapedFileName}</div>` : ''}
  </body>
</html>`);
    printDocument.close();
  }

  private escapeHtml(value: string): string {
    return value.replace(/[&<>"']/g, char => {
      const entities = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      };
      return entities[char];
    });
  }

  private persistRecord(record: SdsRecord): void {
    const index = this.sdsRecords.findIndex(item => item.Id === record.Id);
    if (index > -1) {
      this.sdsRecords[index] = { ...record };
    }

    if (this.selectedSds?.Id === record.Id) {
      this.selectedSds = { ...record };
    }

    if (this.pdfPreviewRecord?.Id === record.Id) {
      this.pdfPreviewRecord = { ...record };
    }
  }

  private async buildBrandedQrDataUrl(qrImageUrl: string): Promise<string> {
    const [qr, logo] = await Promise.all([
      this.loadImage(qrImageUrl),
      this.loadImage('assets/images/KubaLogo.png')
    ]);

    const canvas = document.createElement('canvas');
    canvas.width = QR_PRINT_CANVAS_SIZE;
    canvas.height = QR_PRINT_CANVAS_SIZE;
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(qr, 0, 0, canvas.width, canvas.height);

    const circleDiameter = Math.round(QR_PRINT_CANVAS_SIZE * 0.22);
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    ctx.beginPath();
    ctx.arc(cx, cy, circleDiameter / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fillStyle = '#ffffff';
    ctx.fill();

    const logoMax = Math.round(QR_PRINT_CANVAS_SIZE * 0.14);
    const logoAspect = logo.width / logo.height || 1;
    let drawWidth = logoMax;
    let drawHeight = logoMax;

    if (logoAspect > 1) {
      drawHeight = logoMax / logoAspect;
    } else {
      drawWidth = logoMax * logoAspect;
    }

    ctx.drawImage(logo, cx - drawWidth / 2, cy - drawHeight / 2, drawWidth, drawHeight);
    return canvas.toDataURL('image/jpeg', QR_PRINT_JPEG_QUALITY);
  }

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      const imageUrl = new URL(src, document.baseURI);
      if (imageUrl.origin !== window.location.origin) {
        image.crossOrigin = 'anonymous';
      }
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error(`Unable to load image: ${imageUrl.href}`));
      image.src = imageUrl.href;
    });
  }

  async downloadPdf(record?: SdsRecord): Promise<void> {
    const selectedRecord = record || this.pdfPreviewRecord;
    if (!selectedRecord) {
      return;
    }

    if (selectedRecord.SdsFileData) {
      const a = document.createElement('a');
      a.href = selectedRecord.SdsFileData;
      a.download = selectedRecord.SdsFileName || 'SDS_Document.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }

    if (selectedRecord.SdsFileUrl || selectedRecord.SelectedFileVersionId) {
      try {
        const file = await this.sdsApiService
          .getDocumentFile(
            selectedRecord.Id,
            selectedRecord.SelectedFileVersionId,
            selectedRecord.SdsFileName,
            'application/pdf',
            true
          )
          .toPromise();

        if (!file) {
          throw new Error('The selected SDS file could not be downloaded.');
        }

        this.triggerFileDownload(file);
      } catch (_) {
        this.messageService.add({
          severity: 'error',
          summary: 'SDS',
          detail: this.translate.instant('SDS_PDF_NOT_AVAILABLE')
        });
      }
    }
  }

  private triggerFileDownload(file: File): void {
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name || 'SDS_Document.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  hasUploadedPdf(record: SdsRecord): boolean {
    return !!record.SdsFileData || !!record.SdsFileUrl;
  }

  hasPdf(record: SdsRecord): boolean {
    return !!record.SdsFileData || !!record.SdsFileUrl;
  }

  /** Build + open the per-row Actions dropdown for a record. */
  openActionsMenu(event: Event, record: SdsRecord): void {
    this.actionMenuItems = this.buildActionMenu(record);
    this.rowActionsMenu?.toggle(event);
  }

  private buildActionMenu(record: SdsRecord): MenuItem[] {
    // Clicking the product name opens the preview panel, so the route to the
    // full record lives here. Archived chemicals open read-only, hence "View".
    const items: MenuItem[] = [
      {
        label: this.translate.instant(this.isArchivedView ? 'VIEW' : 'SDS_EDIT'),
        icon: this.isArchivedView ? 'pi pi-eye' : 'pi pi-pencil',
        command: () => this.openDetail(record)
      }
    ];

    if (this.hasUploadedPdf(record)) {
      items.push({
        label: this.translate.instant('SDS_DOWNLOAD_PDF'),
        icon: 'pi pi-download',
        command: () => this.downloadPdf(record)
      });
    }

    if (!this.isArchivedView) {
      items.push({
        label: this.translate.instant('SDS_ARCHIVE_RECORD'),
        icon: 'pi pi-box',
        styleClass: 'sds-action-danger',
        command: () => this.confirmArchive(record)
      });
    } else {
      if (!record.IsDeleted) {
        items.push({
          label: this.translate.instant('SDS_RESTORE_RECORD'),
          icon: 'pi pi-replay',
          command: () => this.restoreRecord(record)
        });
      }
      if (!record.IsDeleted && !record.HasActiveExposures) {
        items.push({
          label: this.translate.instant('SDS_DELETE_RECORD'),
          icon: 'pi pi-trash',
          styleClass: 'sds-action-danger',
          command: () => this.confirmDelete(record)
        });
      }
    }

    return items;
  }

  private revokePdfBlobUrl(): void {
    this.pdfPreviewUrl = null;
    if (this.pdfBlobUrl) {
      URL.revokeObjectURL(this.pdfBlobUrl);
      this.pdfBlobUrl = null;
    }
  }

  private base64ToBlob(dataUrl: string, contentType: string): Blob {
    const base64 = dataUrl.split(',')[1];
    const byteChars = atob(base64);
    const byteArrays: BlobPart[] = [];

    for (let offset = 0; offset < byteChars.length; offset += 512) {
      const slice = byteChars.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);

      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      byteArrays.push(new Uint8Array(byteNumbers));
    }

    return new Blob(byteArrays, { type: contentType });
  }

  ngOnDestroy(): void {
    this.closePdfPreview();
  }

  confirmDelete(record: SdsRecord): void {
    this.recordToDelete = record;
    this.confirmDeleteVisible = true;
  }

  cancelDelete(): void {
    this.confirmDeleteVisible = false;
    this.recordToDelete = null;
  }

  confirmArchive(record: SdsRecord): void {
    this.recordToArchive = record;
    this.confirmArchiveVisible = true;
  }

  cancelArchive(): void {
    this.confirmArchiveVisible = false;
    this.recordToArchive = null;
  }

  async executeArchive(): Promise<void> {
    if (!this.recordToArchive) {
      return;
    }

    const name = this.recordToArchive.ProductName;
    const id = this.recordToArchive.Id;

    try {
      await this.sdsApiService.archiveDocument(id).toPromise();

      if (this.selectedSds?.Id === id) {
        this.closeSidebar();
      }

      this.confirmArchiveVisible = false;
      this.recordToArchive = null;

      // Re-fetch so the archived chemical drops out of the active list.
      this.fetchPage();

      this.translate.get('SDS_ARCHIVE_SUCCESS', { name }).subscribe(message => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('SDS_ARCHIVE_RECORD'),
          detail: message
        });
      });
    } catch (err: any) {
      const detail =
        err?.error?.detail ||
        err?.error?.title ||
        this.translate.instant('SDS_ARCHIVE_FAILED');
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('SDS_ARCHIVE_RECORD'),
        detail
      });
    }
  }

  /** Restore an archived chemical back to the active list (archived view only). */
  async restoreRecord(record: SdsRecord): Promise<void> {
    const name = record.ProductName;
    try {
      await this.sdsApiService.restoreDocument(record.Id).toPromise();

      if (this.selectedSds?.Id === record.Id) {
        this.closeSidebar();
      }

      // Re-fetch so the restored chemical drops out of the archived list.
      this.fetchPage();

      this.translate.get('SDS_RESTORE_SUCCESS', { name }).subscribe(message => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('SDS_RESTORE_RECORD'),
          detail: message
        });
      });
    } catch (err: any) {
      const detail =
        err?.error?.detail ||
        err?.error?.title ||
        this.translate.instant('SDS_RESTORE_FAILED');
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('SDS_RESTORE_RECORD'),
        detail
      });
    }
  }

  async executeDelete(): Promise<void> {
    if (!this.recordToDelete) {
      return;
    }

    const name = this.recordToDelete.ProductName;
    const id = this.recordToDelete.Id;

    try {
      await this.sdsApiService.deleteDocument(id).toPromise();

      if (this.selectedSds?.Id === id) {
        this.closeSidebar();
      }

      this.confirmDeleteVisible = false;
      this.recordToDelete = null;

      // Re-fetch the current page so the paginator total and rows stay accurate.
      this.fetchPage();

      this.translate.get('SDS_DELETE_SUCCESS', { name }).subscribe(message => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('DELETE'),
          detail: message
        });
      });
    } catch (err: any) {
      const detail =
        err?.error?.detail ||
        err?.error?.title ||
        this.translate.instant('SDS_DELETE_FAILED');
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('DELETE'),
        detail
      });
    }
  }

  getPictogramInfo(id: string): { label: string; ghs: string; svg: string } {
    return this.pictogramMap[id] || { label: id, ghs: '?', svg: '' };
  }
}
